import { useMemo } from 'react';

import { useGetStaticAssetUrl } from '@change-corgi/core-react/assets';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import creditCardWhite from '#src/assets/payment/credit-card-white.svg';
import sepaBlack from '#src/assets/payment/sepa-black.svg';
import sepaWhite from '#src/assets/payment/sepa-white.svg';

import {
	useContributionsPageData,
	useContributionsPageState,
} from '#src/app/pages/accountSettings/contributions/main/pageContext';
import type { PaymentType } from '#src/app/shared/utils/payments';

type PaymentMethodOptions = {
	label: string;
	icon: string;
	/** Dark-mode variant of `icon`. When set, the caller should swap to this in dark mode instead of using `icon` unconditionally. */
	iconDark?: string;
	type: PaymentType;
};

type Result = ModelHookResult<
	{
		availablePaymentMethods: PaymentMethodOptions[];
		selectedPaymentType: PaymentType;
	},
	{
		setSelectedPaymentType: (paymentType: PaymentType) => void;
	}
>;

const isAllowedPaymentMethod = (type: string): type is Extract<PaymentType, 'creditCard' | 'sepa'> => {
	return type === 'creditCard' || type === 'sepa';
};

const mapToPaymentMethodOptions = (
	type: PaymentType,
	translate: ReturnType<typeof useI18n>['translate'],
	getStaticAssetUrl: ReturnType<typeof useGetStaticAssetUrl>,
): PaymentMethodOptions => {
	if (type === 'creditCard') {
		return {
			label: translate('fe.components.payments.types.credit_card'),
			icon: getStaticAssetUrl('/payment-options/btn/credit-card-new.svg'),
			iconDark: creditCardWhite,
			type: 'creditCard',
		};
	}

	if (type === 'sepa') {
		return {
			label: translate('fe.components.payments.types.sepa'),
			icon: sepaBlack,
			iconDark: sepaWhite,
			type: 'sepa',
		};
	}

	throw new Error(`Unknown payment type: ${type}`);
};

export function usePaymentMethodSelectorWrapper(): Result {
	const {
		data: {
			featureConfigs: { paymentMethodsEnabledFor },
		},
	} = useContributionsPageData();
	const track = useTracking();
	const { translate } = useI18n();

	const getStaticAssetUrl = useGetStaticAssetUrl();
	const [{ selectedPaymentType }, { setSelectedPaymentType: setSelectedPayment, setUserName }] =
		useContributionsPageState();

	const availablePaymentMethods = useMemo(() => {
		if (!paymentMethodsEnabledFor) return [];
		return paymentMethodsEnabledFor
			.filter((paymentMethod) => isAllowedPaymentMethod(paymentMethod.payment_method_name))
			.map((paymentMethod) => {
				return mapToPaymentMethodOptions(paymentMethod.payment_method_name, translate, getStaticAssetUrl);
			});
	}, [paymentMethodsEnabledFor, translate, getStaticAssetUrl]);

	const setSelectedPaymentType = (paymentType: PaymentType) => {
		void track('sponsor_cc_update_select_payment_type');
		setUserName('');
		setSelectedPayment(paymentType);
	};

	return {
		data: {
			availablePaymentMethods,
			selectedPaymentType,
		},
		actions: {
			setSelectedPaymentType,
		},
	};
}
